import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";

const TEXT = {
  look: { kicker: "Scelti dalla stylist Minime", bg: "bg-[#FBF3EE] border-[#F0DDD3]", cta: "Aggiungi il look alla lista" },
  bag: { kicker: "La valigia consigliata da Minime", bg: "bg-white border-[#EAE3DC]", cta: "Aggiungi la valigia alla lista" },
};

const Item = ({ it, kind }) => (
  <Link to={`/prodotto/${it.id}`} data-testid={`curated-${kind}-item-${it.id}`}
    className="group rounded-2xl bg-white border border-[#EAE3DC] overflow-hidden flex flex-col hover:-translate-y-0.5 transition-transform">
    <div className="relative aspect-square overflow-hidden bg-[#F4EFEA]">
      {it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
      {kind === "bag" && it.quantity > 1 && <span className="absolute top-2 right-2 rounded-full bg-[#2D2825] text-white text-[11px] px-2 py-0.5">x{it.quantity}</span>}
      {it.best_seller && <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-[#E9C46A] text-[#2D2825] text-[10px] px-2 py-0.5"><Star size={10} className="fill-current" /> Best seller</span>}
    </div>
    <div className="p-3 flex flex-col gap-1 flex-1">
      <span className="text-sm text-[#2D2825] line-clamp-2">{it.title}</span>
      {it.reason && <span className="text-[11.5px] text-[#8C7F78] line-clamp-2">{it.reason}</span>}
      <div className="mt-auto pt-1 flex items-center justify-between gap-2">
        <span className="font-serif text-lg text-[#C87D75]">€ {String(it.price).replace(".", ",")}</span>
        {it.sizes?.[0] && <span className="text-[11px] text-[#5C534E] rounded-full bg-[#F4EFEA] px-2 py-0.5">{it.sizes[0]}</span>}
      </div>
    </div>
  </Link>
);

const SetCard = ({ s, kind, onAdd }) => {
  const total = s.items.reduce((t, i) => t + (parseFloat(i.price) || 0) * (kind === "bag" ? i.quantity : 1), 0);
  return (
    <div className={`rounded-3xl border p-4 sm:p-8 overflow-hidden ${TEXT[kind].bg}`} data-testid={`curated-${kind}-${s.id}`}>
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C87D75]">{TEXT[kind].kicker}</span>
      <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-[#2D2825]">{s.title}</h3>
      {s.description && <p className="mt-2 text-sm text-[#5C534E] max-w-xl">{s.description}</p>}
      <div className="mt-6 mb-rail sm:grid sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
        {s.items.map((it) => <Item key={it.id} it={it} kind={kind} />)}
      </div>
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="text-sm text-[#5C534E]">{s.items.length} {s.items.length === 1 ? "capo" : "capi"} · totale <strong>€ {total.toFixed(2).replace(".", ",")}</strong></span>
        <button onClick={() => onAdd(s)} data-testid={`curated-${kind}-add-${s.id}`}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white px-6 min-h-[48px] text-sm transition-colors">
          <Heart size={15} /> {TEXT[kind].cta}
        </button>
      </div>
    </div>
  );
};

export const CuratedSets = ({ kind }) => {
  const { profile, addManyToWishlist } = useProfile();
  const [sets, setSets] = useState([]);
  useEffect(() => {
    if (!profile) return;
    api.get(`/curated/${profile.id}`, { params: { kind } }).then(({ data }) => setSets(data.sets)).catch(() => setSets([]));
  }, [profile?.id, profile?.sex, profile?.due_date, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile || !sets.length) return null;
  const add = (s) => addManyToWishlist(s.items.map((i) => ({ ...i, color_chosen: !!i.color })));
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 space-y-6" data-testid={`curated-${kind}-section`}>
      {sets.map((s) => <SetCard key={s.id} s={s} kind={kind} onAdd={add} />)}
    </section>
  );
};
